import {Checkbox, FormControlLabel, Grid, Typography} from '@material-ui/core';
import {zodResolver} from '@hookform/resolvers/zod';

import {observable, runInAction} from 'mobx';
import {observer} from 'mobx-react-lite';
import {useMemo, useState} from 'react';
import {formatCurrency} from '../../Core/helpers/formatCurrency';
import {Parameter, ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import {Quote} from '../../Core/mobx/ParameterStore/Quote';
import CustomerPortalStore from '../Customer/CustomerPortalStore/CustomerPortalStore';
import {QuoteCombinationInfo} from './QuoteCombinationInfo';
import {QuoteQuestions} from './QuoteQuestions';
import {QuotesHistory} from './QuotesHistory';
import {FormProvider, useForm} from 'react-hook-form';
import {createProsperSchema, ProsperFormValues} from './ProductTabs/lib/validation';
import {defaultValues} from './ProductTabs/lib/defaultValues';
import {IS_PRODUCTION} from '@/constants';
import RootStore from '@/Core/mobx/RootStore';

function cloneQuote(original: Quote): Quote {
    const newQuote = new Quote();

    Object.keys(original).forEach((key) => {
        const typedKey = key as keyof Quote;
        const value = original[typedKey];

        if (value instanceof Parameter) {
            // @ts-ignore
            newQuote[typedKey] = new Parameter(typedKey, value.value);
        } else {
            // @ts-ignore
            newQuote[typedKey] = value;
        }
    });

    return newQuote;
}

export const QuotePanel = observer(
    ({
        parameterStore,
        customerPortalStore,
    }: {
        parameterStore: ParameterStore;
        customerPortalStore: CustomerPortalStore;
    }) => {
        const prosperForm = useForm<ProsperFormValues>({
            resolver: zodResolver(
                createProsperSchema(parameterStore.prequal.age.value, parameterStore.approval.gender.value),
            ),
            mode: 'onChange',
            defaultValues: defaultValues(parameterStore),
        });

        const [combination, setCombination] = useState(false);
        const productType = parameterStore.getQuote()?.productType.value;
        const warningOffset =
            (!IS_PRODUCTION ? 76 : 0) +
            (parameterStore != null && !RootStore.currentAgentStates.includes(parameterStore.approval.state.value)
                ? 80
                : 0);
        const quoteHistory = parameterStore?.quoteHistory;
        const singleQuote = quoteHistory?.length ? quoteHistory[quoteHistory?.length - 1].singleQuote : null;
        const combinedQuote = quoteHistory?.length ? quoteHistory[quoteHistory?.length - 1].combinedQuote : null;
        const topInfo = (
            <>
                {!!parameterStore.walkthroughData?.approvalQuote && (
                    <>
                        <Typography variant="h6" style={{textAlign: 'left'}}>
                            Walkthrough quote
                        </Typography>
                        <Grid style={{marginTop: 10}}>
                            <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                Quote: <strong>{parameterStore.walkthroughData?.approvalQuote}</strong>
                            </Typography>
                            {parameterStore.walkthroughData[parameterStore.walkthroughData?.approvalQuote]
                                ?.face_amount && (
                                <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                    Face amount:{' '}
                                    <strong>
                                        {formatCurrency(
                                            parameterStore.walkthroughData[
                                                parameterStore.walkthroughData?.approvalQuote
                                            ]?.face_amount,
                                        )}
                                    </strong>
                                </Typography>
                            )}
                            {parameterStore.walkthroughData[parameterStore.walkthroughData?.approvalQuote]
                                ?.face_amount_term && (
                                <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                    Face amount term:{' '}
                                    <strong>
                                        {formatCurrency(
                                            parameterStore.walkthroughData[
                                                parameterStore.walkthroughData?.approvalQuote
                                            ]?.face_amount_term,
                                        )}
                                    </strong>
                                </Typography>
                            )}
                            {parameterStore.walkthroughData[parameterStore.walkthroughData?.approvalQuote]
                                ?.face_amount_retirement && (
                                <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                    Face amount retirement:{' '}
                                    <strong>
                                        {formatCurrency(
                                            parameterStore.walkthroughData[
                                                parameterStore.walkthroughData?.approvalQuote
                                            ]?.face_amount_retirement,
                                        )}
                                    </strong>
                                </Typography>
                            )}
                            {parameterStore.walkthroughData[parameterStore.walkthroughData?.approvalQuote]
                                ?.face_amount_total && (
                                <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                    Face amount total:{' '}
                                    <strong>
                                        {formatCurrency(
                                            parameterStore.walkthroughData[
                                                parameterStore.walkthroughData?.approvalQuote
                                            ]?.face_amount_total,
                                        )}
                                    </strong>
                                </Typography>
                            )}
                            {parameterStore.walkthroughData[parameterStore.walkthroughData?.approvalQuote]?.carriers
                                ?.length && (
                                <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                    Carrier:{' '}
                                    <strong>
                                        {parameterStore.walkthroughData[
                                            parameterStore.walkthroughData?.approvalQuote
                                        ]?.carriers.join(', ')}
                                    </strong>
                                </Typography>
                            )}
                            {parameterStore.walkthroughData[parameterStore.walkthroughData?.approvalQuote]?.riders
                                ?.length && (
                                <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                    Riders:{' '}
                                    <strong>
                                        {parameterStore.walkthroughData[
                                            parameterStore.walkthroughData?.approvalQuote
                                        ]?.riders.join(', ')}
                                    </strong>
                                </Typography>
                            )}
                            {parameterStore.walkthroughData[parameterStore.walkthroughData?.approvalQuote]
                                ?.contribution && (
                                <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                    Contribution:{' '}
                                    <strong>
                                        {formatCurrency(
                                            parameterStore.walkthroughData[
                                                parameterStore.walkthroughData?.approvalQuote
                                            ]?.contribution,
                                        )}
                                    </strong>
                                </Typography>
                            )}
                            {parameterStore.walkthroughData[parameterStore.walkthroughData?.approvalQuote]
                                ?.total_contribution && (
                                <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                    Total contribution:{' '}
                                    <strong>
                                        {formatCurrency(
                                            parameterStore.walkthroughData[
                                                parameterStore.walkthroughData?.approvalQuote
                                            ]?.total_contribution,
                                        )}
                                    </strong>
                                </Typography>
                            )}
                            {parameterStore.walkthroughData[parameterStore.walkthroughData?.approvalQuote]?.vul && (
                                <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                    Vul:{' '}
                                    <strong>
                                        {formatCurrency(
                                            parameterStore.walkthroughData[
                                                parameterStore.walkthroughData?.approvalQuote
                                            ]?.vul,
                                        )}
                                    </strong>
                                </Typography>
                            )}
                            {parameterStore.walkthroughData[parameterStore.walkthroughData?.approvalQuote]?.spx && (
                                <Typography variant="subtitle1" style={{textAlign: 'left'}}>
                                    Spx:{' '}
                                    <strong>
                                        {formatCurrency(
                                            parameterStore.walkthroughData[
                                                parameterStore.walkthroughData?.approvalQuote
                                            ]?.spx,
                                        )}
                                    </strong>
                                </Typography>
                            )}
                        </Grid>
                    </>
                )}
                {productType !== 'Prosper' && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={combination}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        runInAction(() => {
                                            const clonedQuote = cloneQuote(parameterStore.quote);
                                            clonedQuote.isCombination = true;
                                            parameterStore.quoteCombination = observable(clonedQuote);
                                        });
                                    } else {
                                        runInAction(() => {
                                            parameterStore.quoteCombination = new Quote();
                                            parameterStore.combinedQuoteData = null;
                                        });
                                    }
                                    /* eslint-enable no-param-reassign */
                                    setCombination((prev) => !prev);
                                }}
                                name="checkedB"
                                color="primary"
                            />
                        }
                        label="Combination"
                    />
                )}
            </>
        );

        const gridContainerStyle = useMemo(
            () => ({height: `calc(100vh - ${135 + warningOffset}px)`, overflowY: 'hidden' as const}),
            [warningOffset],
        );

        if (parameterStore === null) return null;

        return (
            <FormProvider {...prosperForm}>
                <Grid container style={combination ? {} : gridContainerStyle}>
                    <Grid container item xs={12} style={combination ? {marginTop: 20} : {}}>
                        {combination && (
                            <>
                                {topInfo}
                                <QuoteCombinationInfo singleQuote={singleQuote} combinedQuote={combinedQuote} />
                            </>
                        )}
                        <Grid container item xs={12} spacing={3}>
                            {combination ? (
                                <>
                                    <Grid item xs={6}>
                                        <div style={{textAlign: 'center', width: '100%'}}>
                                            <h2>Quote A</h2>
                                        </div>
                                        <QuoteQuestions
                                            quoteParams={parameterStore.quote}
                                            formType="single-quote"
                                            parameterStore={parameterStore}
                                            customerPortalStore={customerPortalStore}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <div style={{textAlign: 'center', width: '100%'}}>
                                            <h2>Quote B</h2>
                                        </div>
                                        <QuoteQuestions
                                            quoteParams={parameterStore.quoteCombination}
                                            formType="combination-quote"
                                            parameterStore={parameterStore}
                                            customerPortalStore={customerPortalStore}
                                        />
                                    </Grid>
                                </>
                            ) : (
                                <Grid item xs={12}>
                                    <QuoteQuestions
                                        quoteParams={parameterStore.quote}
                                        formType="single-quote"
                                        parameterStore={parameterStore}
                                        customerPortalStore={customerPortalStore}
                                        nestOnTop={
                                            <>
                                                {topInfo}
                                                <QuoteCombinationInfo
                                                    singleQuote={singleQuote}
                                                    combinedQuote={combinedQuote}
                                                />
                                            </>
                                        }
                                        nestOnBottom={
                                            <Grid item xs={12} style={{marginTop: 20}}>
                                                <h3>History</h3>
                                                <QuotesHistory parameterStore={parameterStore} />
                                            </Grid>
                                        }
                                    />
                                </Grid>
                            )}
                        </Grid>
                        {combination && (
                            <Grid item xs={12} style={{marginTop: 20}}>
                                <h3>History</h3>
                                <QuotesHistory parameterStore={parameterStore} />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </FormProvider>
        );
    },
);
